import { omit } from 'lodash';
import './aiSubtitleSetting.scss';
import { components } from 'common';
import { lang } from '@umu/ukit-lang';
import { isMobile } from '@umu/umu-ua';
import { useUpdateEffect } from 'ahooks';
import { Iconfont, Switch, Popover, Tooltip } from '@umu/umu-design';
import { translateSettingState } from '../zustand';
import React, { useEffect, useState } from 'react';
import { TranslateSettingState } from "../interface";
import { MoreSettingCurrentOperation } from '../dataflow';
import { updUserAiSubtitleSetupService } from "../service";
import { emitCustomEvent as emitCustomEventDefault } from "../emitCustomEvent";
import AiSubtitleTranslateSettingPanel from '../aiSubtitleTranslateSettingPanel/aiSubtitleTranslateSettingPanel';

const { If } = components;

export type AISubtitleSetting = {
    uControlBar: any;
    emitCustomEvent?: any;
    isInMoreSetting?: boolean;
    aiSubtitleSwitchChecked?: boolean;
    setMoreCurrentOption?: (value: any) => void;
    setAiSubtitleSwitchChecked?: (value: boolean)=> void;
};

function AISubtitleSetting(props: AISubtitleSetting) {
    const { uControlBar, isInMoreSetting = true, emitCustomEvent = emitCustomEventDefault, setMoreCurrentOption,aiSubtitleSwitchChecked,setAiSubtitleSwitchChecked } = props;

    const [switchChecked, setSwitchChecked] = useState(aiSubtitleSwitchChecked ?? true);
    const [popoverVisible, setPopoverVisible] = useState(false);
    const [setting, setSetting] = translateSettingState((state: TranslateSettingState) => [
        state.setting,
        state.setSetting,
    ]);

    const handleSwitchChange = (checked: boolean) => {
        setSwitchChecked(checked);
        setAiSubtitleSwitchChecked?.(checked);

        if(uControlBar?.wrapContainer){
            emitCustomEvent('u-click:subtitleSet', uControlBar.wrapContainer, { active: !checked });
        }
    };

    const handleClickTranslateSettingIcon = () => {
        if (setMoreCurrentOption) {
            setMoreCurrentOption(MoreSettingCurrentOperation.aiSubTitleTranslate);
        }
        setPopoverVisible((prev) => !prev);
    };

    const addEventListenerBar = ()=>{
        uControlBar?.wrapContainer?.addEventListener('u-change:bar',(e:any)=>{
            const {detail:{isActive}} = e;

            if(!isActive){
                setPopoverVisible(false);
            }
        });

        uControlBar?.wrapContainer?.addEventListener('u-click:popover-emptySpace', (_e: any) => {
            setPopoverVisible(false);
        });
    }

    const mergeDisplayOriginal = (setup_lang: string, display_original: any)=>{
        const translateSetting = uControlBar?.config.subtitleConfig.translateSetting;

        if(isMobile){
            return setting.display_original;
        }

        if(display_original != null){
            return display_original;
        }

        return translateSetting.trans_lang?.includes(setup_lang) && translateSetting.trans_lang?.includes(setting.setup_lang) ? setting.display_original : 0;
    }

    const addEventListenerChangeSetupLang = ()=>{
        uControlBar?.wrapContainer.addEventListener('ai-subtitle-change-setup-lang', (e: any) => {
            const {
                detail: { setup_lang, display_original: display_original_prop },
            } = e;

            const translateSetting = uControlBar?.config.subtitleConfig.translateSetting;
            let display_original = mergeDisplayOriginal(setup_lang,display_original_prop)

            setSetting({
                ...setting,
                setup_lang,
                display_original,
                lang: translateSetting.lang,
                trans_lang: translateSetting.trans_lang,
            });
        });

        uControlBar?.wrapContainer.addEventListener('ai-subtitle-translate-change', (e: any) => {
            const {
                detail: { setup_lang },
            } = e;

            setSetting({
                ...setting,
                setup_lang,
            });
        });
    }

    const handleUpdUserAiSubtitleSetup = ()=>{
        if(uControlBar?.config?.subtitleConfig?.isNotSaveTranslateSetting){
            return;
        }

        if(!Object.keys(setting).every(key => setting[key] != undefined)){
            return;
        }

        const params: {[key: string]: any} = {
            setup: JSON.stringify(omit(setting,['lang','trans_lang','apply_to_learner']))
        }

        params.session_id = uControlBar.config.sessionId;

        if(!uControlBar?.config?.subtitleConfig?.translateSettingShow?.isHiddenApplyToLearner){
            params.apply_to_learner = setting.apply_to_learner;
        }

        updUserAiSubtitleSetupService(params);
    }

    const isHiddenTranslateSettingBase = ()=>{
        const transLangStatus = uControlBar?.config?.subtitleInfo?.trans_lang?.status;
        const transLangList: any[] = uControlBar?.config?.subtitleInfo?.trans_lang?.list || [];
        return Number(transLangStatus) !== 2 || !transLangList.every(item=> Number(item.status) === 2);
    }

    const handleSettingDefault = ()=>{
        if(!uControlBar?.config?.subtitleConfig?.translateSetting || uControlBar?.config?.subtitleConfig?.isNotSaveTranslateSetting || isInMoreSetting){
            return;
        }

        const translateSetting = uControlBar.config.subtitleConfig.translateSetting;
        const isHiddenTranslateSetting = isHiddenTranslateSettingBase();

        if(!isHiddenTranslateSetting){
            setSetting({
                lang: translateSetting.lang,
                trans_lang: translateSetting.trans_lang,
                setup_lang: translateSetting.setup_lang,
                font_size: String(translateSetting.font_size),
                trans_pos: String(translateSetting.trans_pos),
                original_pos: String(translateSetting.original_pos),
                display_original: Number(translateSetting.display_original),
                apply_to_learner: Number(translateSetting?.apply_to_learner || '0'),
            });
            return
        }
        setSetting({
            ...setting,
            lang: translateSetting.lang,
            trans_lang: translateSetting.trans_lang,
            setup_lang: translateSetting.lang,
        });
    }

    useEffect(() => {
        addEventListenerBar();
        addEventListenerChangeSetupLang();
    },[setting]);

    useUpdateEffect(()=>{
        !isInMoreSetting && !popoverVisible && handleUpdUserAiSubtitleSetup();
    },[popoverVisible]);

    useEffect(()=>{
        handleSettingDefault();
    },[uControlBar?.config?.subtitleConfig])

    /**
     * @description: 兼容 Angular
     * 奇怪的现象: 在 Angular 中的 topLeft = React 中的 topRight
     */

    const popoverPlacement = uControlBar?.config?.subtitleConfig?.from === 'angular' ? 'topRight': 'topLeft';

    const isHiddenSetupSetting = (uControlBar?.config?.subtitleConfig?.translateSettingShow?.isHiddenSubtitleSetting ?? false);
    const isHiddenTranslateSetting = isHiddenTranslateSettingBase();

    if(isHiddenSetupSetting){
        return null;
    }

    return (
        <>
            {/* 在更多设置中 */}
            <If condition={isInMoreSetting}>
                <div
                    className={`u-ai-subtitle-switch-and-translate-setting is-in-more-setting ${isMobile ? 'm' : 'pc'}`}
                >
                    {/* AI 字幕开关 */}
                    <div className="u-ai-subtitle-switch">
                        <span className="u-bar-ai-subtitle-switch-label">{lang('AI 字幕')}</span>
                        <If condition={isMobile}>
                            <span
                                className={`u-ai-subtitle-switch-content ${switchChecked ? 'active' : ''}`}
                                onClick={() => handleSwitchChange(!switchChecked)}
                            ></span>
                        </If>
                        <If condition={!isMobile}>
                            <Switch checked={switchChecked} onChange={handleSwitchChange} style={{ margin: 0 }} />
                        </If>
                    </div>
                    <If condition={switchChecked && !isHiddenTranslateSetting}>
                        <div className="u-ai-subtitle-translate-setting" onClick={handleClickTranslateSettingIcon}>
                            <div className="translate-setting-label">{lang('字幕翻译设置')}</div>
                            <div className="translate-setting-operation">
                                <Iconfont type="SmallMore" />
                            </div>
                        </div>
                    </If>
                </div>
            </If>
            {/* 不在更多设置中 */}
            <If condition={!isInMoreSetting}>
                <span className="u-ai-subtitle-switch-and-translate-setting">
                    {/* AI 字幕开关 */}
                    <span className="u-ai-subtitle-switch">
                        <span className="u-bar-ai-subtitle-switch-label">{lang('AI 字幕')}</span>
                        <Switch checked={switchChecked} onChange={handleSwitchChange} style={{ margin: 0 }} />
                    </span>
                    {/* AI 字幕翻译设置 */}
                    <If condition={switchChecked && !isHiddenTranslateSetting}>
                        <span className="u-ai-subtitle-translate-setting">
                            <Popover
                                hideArrow
                                trigger="click"
                                visible={popoverVisible}
                                placement={popoverPlacement}
                                content={
                                    <AiSubtitleTranslateSettingPanel
                                        setting={setting}
                                        setSetting={setSetting}
                                        visible={popoverVisible}
                                        uControlBar={uControlBar}
                                        emitCustomEvent={emitCustomEvent}
                                    />
                                }
                                overlayClassName="u-ai-subtitle-translate-setting-popover"
                                getPopupContainer={(triggerNode: HTMLElement) => triggerNode}
                                destroyTooltipOnHide={true}
                            >
                                <span
                                    className="u-ai-subtitle-translate-setting-icon"
                                    onClick={handleClickTranslateSettingIcon}
                                >
                                    <Tooltip title={lang('字幕翻译设置')} placement="bottom">
                                        <Iconfont type="Subtitle" />
                                    </Tooltip>
                                </span>
                            </Popover>
                        </span>
                    </If>
                </span>
            </If>
        </>
    );
}

export default AISubtitleSetting;
